import {useCallback} from 'react';
import type {RoleEnum} from 'src/api/ProactApiClient';
import {useAppSelector} from 'src/redux/hooks';
import type {
  ContextualEntity,
  ContextualPermissionDef} from './contextualTypes';
import {
  ContextualPredicateType,
} from './contextualTypes';

/**
 * Hook that provides contextual permission evaluation functions.
 * Used to evaluate entity-level permissions based on the current user's context.
 *
 * @example
 * const {canPerform} = useContextualPermissionEvaluator();
 *
 * // Check if user can perform a transition
 * const canPublish = canPerform(jhaEntity, transition.contextualPermissions);
 */
export function useContextualPermissionEvaluator() {
  const {session, isSuperAdmin} = useAppSelector(state => state.session);
  const user = session?.user;

  /**
   * Evaluates a single contextual permission predicate.
   */
  const evaluatePredicate = useCallback(
    <T extends ContextualEntity>(
      entity: T,
      permission: ContextualPermissionDef
    ): boolean => {
      if (!user) return false;

      switch (permission.predicate) {
        case ContextualPredicateType.IsCreator:
          return (
            entity.creatorId === user.id || entity.creator?.id === user.id
          );

        case ContextualPredicateType.IsSupervisorOfCreator:
          return (
            entity.creatorSupervisorId === user.id ||
            entity.creator?.supervisorId === user.id
          );

        case ContextualPredicateType.IsAssignee:
          return entity.assigneeId === user.id;

        case ContextualPredicateType.HasRole:
          if (!permission.requiredRoles || permission.requiredRoles.length === 0)
            return false;
          if (isSuperAdmin) return true;
          return permission.requiredRoles.includes(user.roleId as RoleEnum);

        case ContextualPredicateType.SameDepartment:
          // SameDepartment check requires departmentId which is not available
          // in the current UserSessionResponse. This predicate should be
          // evaluated on the backend instead.
          console.warn(
            'SameDepartment predicate cannot be evaluated on frontend - missing user.departmentId'
          );
          return false;

        case ContextualPredicateType.InUserGroup:
          // InUserGroup check requires userGroupMemberships which is not available
          // in the current UserSessionResponse. This predicate should be
          // evaluated on the backend instead.
          console.warn(
            'InUserGroup predicate cannot be evaluated on frontend - missing user.userGroupMemberships'
          );
          return false;

        case ContextualPredicateType.Custom:
          // Custom evaluators not yet implemented on frontend
          return false;

        default:
          return false;
      }
    },
    [user, isSuperAdmin]
  );

  /**
   * Evaluates whether the current user can perform an action based on contextual permissions.
   *
   * @param entity - The entity to evaluate against
   * @param permissions - Array of contextual permission requirements
   * @param requireAll - If true, ALL permissions must be satisfied (AND logic).
   *                     If false (default), ANY permission satisfies the requirement (OR logic).
   * @returns true if the user has permission, false otherwise
   */
  const canPerform = useCallback(
    <T extends ContextualEntity>(
      entity: T,
      permissions: ContextualPermissionDef[] | undefined,
      requireAll = false
    ): boolean => {
      // Super admins bypass all contextual checks, matching the backend
      // ProactAuthorize attribute which authorizes super admins for every module.
      if (isSuperAdmin) return true;

      // No contextual permissions defined = anyone with module permission can perform
      if (!permissions || permissions.length === 0) return true;

      const results = permissions.map(p => evaluatePredicate(entity, p));

      return requireAll ? results.every(Boolean) : results.some(Boolean);
    },
    [evaluatePredicate, isSuperAdmin]
  );

  return {evaluatePredicate, canPerform};
}
